<template>
  <div class="white-bg p2">
    <!-- stepper -->
    <div v-if="isValidSession || loading">
      <div v-if="activeStep?.canShowBackButton && !isFirstVisibleStep" class="mt3 pointer" @click="handleBack">
        <button class="btn-cta-text border-none light-grey-bg p1">
          <i class="icon icon-angle-left-icon mr0"></i> Retour
        </button>
      </div>
      <ul v-if="activeStep?.canShowBackButton" class="xs-hide flex justify-center progressbar col-12 items-end" style="list-style-type: none; padding-inline-start:0;">
        <template v-for="step in steps">
          <li
            v-if="step.show"
            :key="step.key"
            class="px1"
            :class="[{ 'pointer': step.visited }]"
            @click="handleBackStep(step)"
          >
            <div class="stepper-dot" :class="{ 'active': step.isActive || step.visited, 'disabled': !step.visited }">
            </div>
          </li>
        </template>
      </ul>
      <h3 class="center m0"> {{ route.meta.title || '' }} </h3>
    </div>

    <router-view v-if="!loading" />

    <Loader v-else />
  </div>
</template>

<script setup>
import { ref, provide, computed, onBeforeMount } from 'vue';
import { useRoute } from 'vue-router';
import { changeToPage, checkCustomerUnpaidStatus, handleProduct } from '@/utils/logics';

import Loader from '@/components/elements/Loader.vue';

import { useStore } from '@/store/index';

const UNPAID_MESSAGE = 'Une commande en impayé est associée à votre compte. Merci de régulariser votre situation avant de réserver à nouveau.';
const UNPAID_CHECK_ERROR_MESSAGE = 'Nous ne parvenons pas à vérifier votre situation de paiement pour le moment. Merci de réessayer ultérieurement.';

const store = useStore();

const route = useRoute();

const loading = ref(false);

const offerInfo = computed(() => store.state.offerInfo);
const siteInfo = computed(() => store.state.siteInfo);
const isInvite = computed(() => store.state.courtSettings.isInvite || false);

const isValidSession = computed(() => (store.state.courtSettings.hasOwnProperty('isValidSession') ? store.state.courtSettings.isValidSession : true));

const session = computed(() => store.state.courtSettings.session);

const stepsBase = ref([
  { index: 0, key: 'CourtProducts', visited: false, isActive: false, show: true, canShowBackButton: true },
  { index: 1, key: 'CourtSessions', visited: false, isActive: false, show: true, canShowBackButton: true },
  { index: 2, key: 'CourtParticipants', visited: false, isActive: false, show: true, canShowBackButton: true },
  { index: 3, key: 'CourtOptions', visited: false, isActive: false, show: true, canShowBackButton: true },
  { index: 4, key: 'CourtPayment', visited: false, isActive: false, show: true, canShowBackButton: true },
  { index: 5, key: 'CourtConfirmation', visited: false, isActive: false, show: true, canShowBackButton: false }
]);

const steps = computed(() => stepsBase.value);

const activeStep = computed(() => steps.value.find(step => step.key === route.name) || stepsBase.value.find(s => s.show));

const isFirstVisibleStep = computed(() => {
  const firstVisible = steps.value.find(step => step.show);
  return firstVisible.key === activeStep.value.key;
});

const handleStep = receivedStep => {
  stepsBase.value = stepsBase.value.map(step => ({
    ...step,
    isActive: step.key === receivedStep.key,
    visited: activeStep.value.index > receivedStep.index ? step.visited : receivedStep.index >= step.index
  }));

  store.commit('setCourtSettings', { ...store.state.courtSettings, currentStep: receivedStep.key });
  if (receivedStep.key !== route.name) {
    changeToPage(receivedStep.key, {}, route.query);
  }
};

const handleBackStep = receivedStep => {
  if (receivedStep.index >= activeStep.value.index && !receivedStep.visited) return;
  handleStep(receivedStep);
};

const handleBack = (step = activeStep?.value) => {
  const stepIndex = step?.index;
  const prevIndex = typeof stepIndex === 'number' ? stepIndex - 1 : undefined;
  const previousStep = steps.value.find(s => s.index === prevIndex);
  if (previousStep && previousStep.show) {
    return handleStep(previousStep);
  }

  return handleBack(previousStep);
};

const advanceStep = (step = activeStep?.value) => {
  const curIndex = step?.index;
  const nextIndex = typeof curIndex === 'number' ? curIndex + 1 : undefined;
  const nextStep = steps.value.find(s => s.index === nextIndex);

  if (nextStep && nextStep.show) {
    return handleStep(nextStep);
  }

  return advanceStep(nextStep);
};

const handleStepVisibility = (step, show) => {
  const foundStepIndex = stepsBase.value.findIndex(s => s.key === step);
  if (foundStepIndex !== -1) {
    stepsBase.value[foundStepIndex].show = show;
  }
};

const setInitialStep = step => {
  const foundStepIndex = stepsBase.value.findIndex(s => s.key === step);
  if (foundStepIndex !== -1) {
    stepsBase.value[foundStepIndex] = {
      ...stepsBase.value[foundStepIndex],
      isActive: true,
      visited: true
    };

    if (route.name !== stepsBase.value[foundStepIndex].key) {
      changeToPage(stepsBase.value[foundStepIndex].key, {}, route.query);
    }
  }
};

const handleInitialOptionStep = () => {
  const tariff = offerInfo.value.detail.prices.find(price => !price.isGymlibTariff);

  const gymLibTariff = offerInfo.value.detail.prices.find(price => price.isGymlibTariff);

  const availableOptions = offerInfo.value.options.filter(option => option.detail.prices.find(price => price.tariffCategoryCode === tariff.tariffCategoryCode)).map(o => ({
    ...o,
    price: o.detail.prices.find(price => price.tariffCategoryCode === tariff.tariffCategoryCode),
    quantity: 0
  }));

  if (!availableOptions.length) {
    handleStepVisibility('CourtOptions', false);
  }

  store.commit('setCourtSettings', { ...store.state.courtSettings, tariff, availableOptions, gymLibTariff });

  return { tariff, availableOptions };
};

const loadPreviousStep = () => {
  if (store.state.courtSettings && store.state.courtSettings.currentStep) {
    const currentStep = stepsBase.value.find(step => step.key === store.state.courtSettings.currentStep);
    stepsBase.value = stepsBase.value.map(step => ({
      ...step,
      isActive: step.key === store.state.courtSettings.currentStep,
      visited: currentStep.index >= step.index
    }));

    if (route.name !== currentStep.key) {
      changeToPage(currentStep.key, {}, route.query);
    }
  }
};

onBeforeMount(async () => {
  loading.value = true;

  const unpaidStatus = await checkCustomerUnpaidStatus(store.state.siteInfo.workspace);

  loading.value = false;

  /* Fails closed: an unverifiable payment situation blocks access just like a confirmed unpaid order. */
  if (!unpaidStatus.success || unpaidStatus.hasUnpaid) {
    store.commit('setCourtSettings', {
      ...store.state.courtSettings,
      isValidSession: false,
      unpaidBookings: unpaidStatus.unpaidBookings,
      unpaidSubscriptions: unpaidStatus.unpaidSubscriptions,
      errorMessage: unpaidStatus.success ? UNPAID_MESSAGE : UNPAID_CHECK_ERROR_MESSAGE
    });
    changeToPage('CourtSessionUnpaid');
    return;
  }

  const { products } = store.state;

  store.commit('setCurrentParticipant', store.state.user);

  let fromProductStep = false;

  const refresh = route?.query?.refresh === 'true';

  if (!refresh) {
    if (products && products.length > 1) {
      handleStepVisibility('CourtProducts', true);
      setInitialStep('CourtProducts');
      return;
    }

    if (products && products.length === 1 && products[0].dynamo_offers.length === 1) {
      loading.value = true;
      const offerId = siteInfo.value && siteInfo.value.is_internal_session ? products[0].dynamo_offers[0].uuid : products[0].dynamo_offers[0].horanet_product.horanet_product_code;
      await handleProduct(products[0].uuid, offerId);
      fromProductStep = true;
      loading.value = false;
    }
  } else if (route.query?.product && route.query?.offer) {
    loading.value = true;
    await handleProduct(route.query.product, route.query.offer);
    fromProductStep = true;
    loading.value = false;
  }

  handleStepVisibility('CourtProducts', false);

  if (!isValidSession.value) {
    changeToPage('CourtSessionUnavailable');
    return;
  }

  handleStepVisibility('CourtSessions', true);
  setInitialStep('CourtSessions');

  handleInitialOptionStep();

  let isCaptainPlaying = store.state.courtSettings.isCaptainPlaying !== undefined ? store.state.courtSettings.isCaptainPlaying : !isInvite.value;

  if (isInvite.value) {
    const sessionStepIndex = stepsBase.value.findIndex(step => step.key === 'CourtSessions');
    isCaptainPlaying = session?.value?.captain?.isPlaying || true;

    if (sessionStepIndex || sessionStepIndex === 0) {
      stepsBase.value[sessionStepIndex] = {
        ...stepsBase.value[sessionStepIndex],
        show: false,
        isActive: false,
        isVisited: false
      };

      if (stepsBase.value[sessionStepIndex + 1]) {
        stepsBase.value[sessionStepIndex + 1] = {
          ...stepsBase.value[sessionStepIndex + 1],
          isActive: true
        };
      }

      advanceStep(stepsBase.value[sessionStepIndex]);
    }
  }

  store.commit('setCourtSettings', { ...store.state.courtSettings, fromProductStep, isCaptainPlaying: isCaptainPlaying });

  const courtShoppingCart = store.state.user?.courtShoppingCart;
  // CB-return is signalled by the store `refresh` flag (set in BaseLayout); the URL query
  // is cleared before this mount, so route.query.refresh cannot be relied on here.
  const cbReturn = refresh || store.state.courtSettings.refresh === true;
  const returningFromCbSave = cbReturn && store.state.courtSettings.currentStep === 'CourtPayment';
  if ((session.value && courtShoppingCart) || returningFromCbSave) {
    // One-shot: pin the payment step and clear the sticky `refresh` flag so a later mount
    // does not re-pin it.
    store.commit('setCourtSettings', { ...store.state.courtSettings, currentStep: 'CourtPayment', refresh: false });
    setInitialStep('CourtPayment');
  }

  loadPreviousStep();
});

provide('advanceStep', advanceStep);
provide('handleStepVisibility', handleStepVisibility);
provide('handleInitialOptionStep', handleInitialOptionStep);
provide('loadPreviousStep', loadPreviousStep);

</script>

<style scoped>
  .progressbar {
    display: flex;
    align-items: center;
    gap: 32px;
  }

  .progressbar li {
    position: relative;
    display: flex;
    align-items: center;
  }

  .progressbar li:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 18px; /* dot width (12px) + some spacing */
    width: 52px; /* same as gap */
    height: 2px;
    background: var(--light-cyan);
  }

  .stepper-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 2px solid var(--light-cyan);
    background: transparent;
    position: relative;
    z-index: 1;

    &.active {
      background: var(--light-cyan);
    }
    &.disabled {
      background: var(--white);
      border: 2px solid var(--light-cyan);
    }
  }
</style>
